import React from "react";
import { motion } from "framer-motion";
import {
  Home, Link2, Send, Copy, CalendarCheck, Bell, ArrowRight, Sparkles, Check, X,
  ClipboardList, Plus, CalendarDays, Users, PawPrint, Briefcase, UserCheck, ShieldCheck, Clock,
} from "lucide-react";
import { BRAND } from "@/lib/api";

const T = {
  ru: {
    a_kicker: "Для агентов",
    a_title_a: "Одна ссылка для собственника —",
    a_title_b: "полный контроль для агента",
    a_lead:
      "Создайте карточку объекта на tagdada.com и просто отправьте ссылку собственнику. Он сам задаёт доступность для показов и подключает свой календарь — никаких созвонов «а когда вам удобно?». Вы получаете уведомление о каждой заявке на просмотр и держите всё под контролем с минимумом действий.",
    a_steps: [
      { t: "Создайте карточку объекта", d: "Фото, характеристики, цена и условия — карточка готова за пару минут на tagdada.com." },
      { t: "Отправьте ссылку собственнику", d: "Одна ссылка в Telegram, WhatsApp или по почте — без регистрации для собственника." },
      { t: "Собственник задаёт доступность", d: "Он отмечает удобные окна и подключает свой календарь. Никакой переписки туда-сюда." },
      { t: "Вы получаете каждую заявку", d: "Арендаторы бронируют свободные слоты, а вы получаете уведомления и ведёте follow-up в один клик." },
    ],
    a_cta: "Создать карточку объекта",
    b_kicker: "Отбор арендаторов",
    b_title_a: "Квалифицируйте арендаторов",
    b_title_b: "ещё до просмотра",
    b_lead:
      "Агент или собственник добавляет к объекту ключевые вопросы. Арендатор отвечает на них при бронировании слота — и вы заранее видите, кто подходит. Подтвердите или отклоните заявку в один клик, не тратя время на лишние показы.",
    b_steps: [
      { t: "Добавьте ключевые вопросы", d: "Дата заезда, число жильцов, питомцы, доход или занятость — выберите то, что важно вам." },
      { t: "Арендатор отвечает при записи", d: "Чтобы забронировать слот, арендатор обязательно отвечает на ваши вопросы." },
      { t: "Изучите ответы рядом с заявкой", d: "Все ответы — в карточке заявки, рядом со временем показа и контактами." },
      { t: "Подтвердите или отклоните", d: "Одно нажатие — и арендатор получает уведомление. Слот освобождается автоматически." },
    ],
    b_cta: "Настроить вопросы бесплатно",
    m: {
      newCard: "Новый объект", apt: "2-комн. квартира, 65 м²", addr: "ул. Тверская, 12", price: "95 000 ₽/мес", publish: "Опубликовать",
      toOwner: "Собственнику", msg: "Здравствуйте! Отметьте, пожалуйста, удобное время для показов:", sent: "Отправлено",
      avail: "Доступность для показов", synced: "Календарь подключён", cals: "Google · Яндекс", days: ["Пн", "Вт", "Ср", "Чт", "Пт"],
      requests: "Заявки на просмотр", req: [
        { n: "Анна К.", s: "Завтра, 18:00", st: "Новая" },
        { n: "Игорь М.", s: "Пт, 12:30", st: "Новая" },
        { n: "Ольга Р.", s: "Сб, 11:00", st: "Подтв." },
      ],
      questions: "Вопросы к арендатору", q: ["Дата заезда", "Количество жильцов", "Питомцы", "Доход / занятость"], addQ: "Добавить вопрос",
      booking: "Запись на просмотр", slot: "Завтра, 18:00",
      ans: [
        { q: "Дата заезда", a: "1 октября" },
        { q: "Жильцов", a: "2 взрослых" },
        { q: "Питомцы", a: "Нет" },
        { q: "Занятость", a: "Полная, IT" },
      ],
      submit: "Записаться",
      review: "Заявка · Анна К.", match: "Подходит по 4 из 4",
      decide: "Решение по заявке", accept: "Подтвердить", decline: "Отклонить", confirmed: "Просмотр подтверждён · Анна К., завтра 18:00",
    },
  },
  en: {
    a_kicker: "For agents",
    a_title_a: "One link for the landlord —",
    a_title_b: "full control for the agent",
    a_lead:
      "Create the property card on tagdada.com and simply send a link to the landlord. They set their viewing availability and connect their calendar themselves — no more \"when suits you?\" calls. You're notified of every viewing request and keep full control with minimal effort.",
    a_steps: [
      { t: "Create the property card", d: "Photos, specs, price and terms — the card is ready in a couple of minutes on tagdada.com." },
      { t: "Send one link to the landlord", d: "One link via Telegram, WhatsApp or email — the landlord doesn't need to sign up." },
      { t: "Landlord sets availability", d: "They mark convenient windows and connect their calendar. No back-and-forth." },
      { t: "You get every request", d: "Tenants book open slots, you're notified and follow up in one click." },
    ],
    a_cta: "Create a property card",
    b_kicker: "Tenant screening",
    b_title_a: "Qualify tenants",
    b_title_b: "before the viewing",
    b_lead:
      "The agent or landlord adds key questions to the property. Tenants answer them when booking a slot — so you know upfront who's a fit. Confirm or decline the request in one click and skip pointless viewings.",
    b_steps: [
      { t: "Add key questions", d: "Move-in date, number of occupants, pets, income or employment — pick what matters to you." },
      { t: "Tenant answers when booking", d: "To book a slot, the tenant must answer your questions." },
      { t: "Review answers with the request", d: "All answers sit in the request, next to the viewing time and contacts." },
      { t: "Confirm or decline", d: "One tap and the tenant is notified. Declined slots free up automatically." },
    ],
    b_cta: "Set up questions for free",
    m: {
      newCard: "New property", apt: "2-room apt, 65 m²", addr: "12 Tverskaya st.", price: "95,000 ₽/mo", publish: "Publish",
      toOwner: "To landlord", msg: "Hi! Please pick convenient times for viewings:", sent: "Sent",
      avail: "Viewing availability", synced: "Calendar connected", cals: "Google · Yandex", days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
      requests: "Viewing requests", req: [
        { n: "Anna K.", s: "Tomorrow, 18:00", st: "New" },
        { n: "Igor M.", s: "Fri, 12:30", st: "New" },
        { n: "Olga R.", s: "Sat, 11:00", st: "Conf." },
      ],
      questions: "Questions for tenants", q: ["Move-in date", "Number of occupants", "Pets", "Income / employment"], addQ: "Add question",
      booking: "Book a viewing", slot: "Tomorrow, 18:00",
      ans: [
        { q: "Move-in", a: "October 1" },
        { q: "Occupants", a: "2 adults" },
        { q: "Pets", a: "None" },
        { q: "Employment", a: "Full-time, IT" },
      ],
      submit: "Book",
      review: "Request · Anna K.", match: "Matches 4 of 4",
      decide: "Request decision", accept: "Confirm", decline: "Decline", confirmed: "Viewing confirmed · Anna K., tomorrow 18:00",
    },
  },
};

/* ---------- Mock screens ---------- */
const Screen = ({ children }) => (
  <div className="relative h-[230px] rounded-2xl bg-gradient-to-br from-[#FFF4EC] to-[#FFE9DC] p-4 overflow-hidden">
    <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/50" />
    <div className="relative h-full">{children}</div>
  </div>
);
const Panel = ({ className = "", children }) => (
  <div className={`rounded-xl bg-white shadow-[0_10px_30px_-15px_rgba(26,18,16,0.35)] border border-orange-100/60 ${className}`}>{children}</div>
);

function MockCreate({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <div className="flex items-center gap-2 text-[11px] font-bold text-slate-500"><Plus className="h-3.5 w-3.5 text-[#F26B2A]" /> {m.newCard}</div>
        <div className="mt-2 flex gap-3">
          <div className="h-[72px] w-[88px] rounded-lg bg-gradient-to-br from-[#F59B42] to-[#F26B2A] flex items-center justify-center shrink-0">
            <Home className="h-8 w-8 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-extrabold text-slate-900 truncate">{m.apt}</p>
            <p className="text-[11px] text-slate-500 truncate">{m.addr}</p>
            <p className="mt-1 text-[13px] font-extrabold text-[#F26B2A]">{m.price}</p>
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          <div className="h-2 rounded-full bg-slate-100 w-full" />
          <div className="h-2 rounded-full bg-slate-100 w-4/5" />
        </div>
        <div className="mt-3 rounded-lg bg-[#F26B2A] text-white text-[12px] font-bold text-center py-2">{m.publish}</div>
      </Panel>
    </Screen>
  );
}

function MockLink({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-full bg-[#229ED9] flex items-center justify-center"><Send className="h-3.5 w-3.5 text-white" /></span>
          <span className="text-[12px] font-bold text-slate-800">{m.toOwner}</span>
        </div>
        <div className="mt-3 ml-auto max-w-[92%] rounded-2xl rounded-tr-sm bg-[#FFF1E8] px-3 py-2.5">
          <p className="text-[11px] text-slate-700 leading-snug">{m.msg}</p>
          <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-white px-2 py-1.5 border border-orange-100">
            <Link2 className="h-3.5 w-3.5 text-[#F26B2A] shrink-0" />
            <span className="text-[11px] font-bold text-[#F26B2A] truncate">tagdada.com/p/8f2k-tverskaya</span>
            <Copy className="h-3 w-3 text-slate-400 ml-auto shrink-0" />
          </div>
        </div>
        <p className="mt-1.5 text-right text-[10px] font-semibold text-emerald-600 flex items-center justify-end gap-1"><Check className="h-3 w-3" />{m.sent}</p>
      </Panel>
    </Screen>
  );
}

function MockAvailability({ m }) {
  const on = [[1, 0, 1], [0, 1, 1], [1, 1, 0], [0, 1, 0], [1, 0, 1]];
  return (
    <Screen>
      <Panel className="p-3">
        <p className="text-[12px] font-extrabold text-slate-900">{m.avail}</p>
        <div className="mt-2.5 grid grid-cols-5 gap-1.5">
          {m.days.map((d, i) => (
            <div key={d} className="flex flex-col items-center gap-1.5">
              <span className="text-[10px] font-bold text-slate-400">{d}</span>
              {on[i].map((v, j) => (
                <span key={j} className={`h-5 w-full rounded-md ${v ? "bg-[#F26B2A]" : "bg-slate-100"}`} />
              ))}
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5">
          <CalendarCheck className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-[11px] font-bold text-emerald-700">{m.synced}</span>
          <span className="ml-auto text-[10px] font-semibold text-slate-500">{m.cals}</span>
        </div>
      </Panel>
    </Screen>
  );
}

function MockRequests({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <div className="flex items-center gap-2">
          <span className="relative">
            <Bell className="h-5 w-5 text-slate-800" />
            <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-[#F26B2A] text-white text-[9px] font-extrabold flex items-center justify-center">2</span>
          </span>
          <span className="text-[12px] font-extrabold text-slate-900">{m.requests}</span>
        </div>
        <div className="mt-2.5 space-y-1.5">
          {m.req.map((r, i) => (
            <div key={r.n} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
              <span className="h-6 w-6 rounded-full bg-[#FFE1CF] text-[#F26B2A] text-[10px] font-extrabold flex items-center justify-center shrink-0">{r.n[0]}</span>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-slate-800 truncate">{r.n}</p>
                <p className="text-[10px] text-slate-500 truncate">{r.s}</p>
              </div>
              <span className={`ml-auto text-[9px] font-extrabold rounded-full px-2 py-0.5 ${i < 2 ? "bg-[#FFF1E8] text-[#F26B2A]" : "bg-emerald-50 text-emerald-600"}`}>{r.st}</span>
            </div>
          ))}
        </div>
      </Panel>
    </Screen>
  );
}

const Q_ICONS = [CalendarDays, Users, PawPrint, Briefcase];

function MockQuestions({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <div className="flex items-center gap-2 text-[12px] font-extrabold text-slate-900"><ClipboardList className="h-4 w-4 text-[#F26B2A]" /> {m.questions}</div>
        <div className="mt-2 space-y-1.5">
          {m.q.map((q, i) => {
            const Icon = Q_ICONS[i];
            return (
              <div key={q} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
                <Icon className="h-3.5 w-3.5 text-[#F26B2A] shrink-0" />
                <span className="text-[11px] font-semibold text-slate-700 truncate">{q}</span>
                <span className="ml-auto h-3.5 w-6 rounded-full bg-[#F26B2A] relative shrink-0"><span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-white" /></span>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] font-bold text-[#F26B2A] flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> {m.addQ}</p>
      </Panel>
    </Screen>
  );
}

function MockAnswer({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-extrabold text-slate-900">{m.booking}</span>
          <span className="flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5"><Clock className="h-3 w-3" />{m.slot}</span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-1.5">
          {m.ans.map((a) => (
            <div key={a.q} className="rounded-lg border border-slate-200 px-2 py-1.5">
              <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 truncate">{a.q}</p>
              <p className="text-[11px] font-semibold text-slate-800 truncate">{a.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-2.5 rounded-lg bg-[#F26B2A] text-white text-[12px] font-bold text-center py-2 flex items-center justify-center gap-1">{m.submit} <ArrowRight className="h-3.5 w-3.5" /></div>
      </Panel>
    </Screen>
  );
}

function MockReview({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-full bg-[#FFE1CF] text-[#F26B2A] text-[11px] font-extrabold flex items-center justify-center">{m.req[0].n[0]}</span>
          <span className="text-[12px] font-extrabold text-slate-900 truncate">{m.review}</span>
        </div>
        <div className="mt-2 space-y-1">
          {m.ans.map((a) => (
            <div key={a.q} className="flex items-center gap-2 text-[11px]">
              <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <span className="text-slate-500 truncate">{a.q}</span>
              <span className="ml-auto font-semibold text-slate-800 truncate">{a.a}</span>
            </div>
          ))}
        </div>
        <div className="mt-2.5 flex items-center gap-2 rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5">
          <UserCheck className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-[11px] font-bold text-emerald-700">{m.match}</span>
        </div>
      </Panel>
    </Screen>
  );
}

function MockDecide({ m }) {
  return (
    <Screen>
      <Panel className="p-3">
        <p className="text-[12px] font-extrabold text-slate-900">{m.decide}</p>
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          <span className="rounded-lg bg-emerald-500 text-white text-[12px] font-bold py-2 flex items-center justify-center gap-1"><Check className="h-3.5 w-3.5" />{m.accept}</span>
          <span className="rounded-lg bg-white border border-slate-200 text-slate-600 text-[12px] font-bold py-2 flex items-center justify-center gap-1"><X className="h-3.5 w-3.5" />{m.decline}</span>
        </div>
      </Panel>
      <Panel className="mt-3 p-2.5 flex items-start gap-2">
        <span className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /></span>
        <p className="text-[11px] font-semibold text-slate-700 leading-snug">{m.confirmed}</p>
      </Panel>
    </Screen>
  );
}

/* ---------- Section ---------- */
function StepsSection({ testid, kicker, titleA, titleB, lead, steps, mocks, cta, tone }) {
  return (
    <section data-testid={testid} className={tone}>
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-white border border-orange-100 text-[#E05A19] text-xs font-extrabold uppercase tracking-[0.15em] px-3.5 py-1.5 shadow-sm">
            {kicker}
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-[-0.02em] text-[#1A1210] leading-[1.08]">
            {titleA} <span className="text-[#F26B2A]">{titleB}</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">{lead}</p>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => {
            const Mock = mocks[i];
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }} data-testid={`${testid}-step-${i}`}
                className="relative rounded-[24px] bg-white border border-orange-100/80 p-3 shadow-[0_20px_50px_-30px_rgba(242,107,42,0.4)] flex flex-col">
                <Mock />
                <div className="px-2 pt-5 pb-3">
                  <div className="flex items-center gap-3">
                    <span className="h-9 w-9 rounded-full bg-gradient-to-br from-[#F26B2A] to-[#F59B42] text-white text-sm font-extrabold flex items-center justify-center shadow-md shrink-0">{i + 1}</span>
                    {i < steps.length - 1 ? <span className="hidden lg:block h-px flex-1 border-t-2 border-dashed border-orange-200" /> : null}
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold text-slate-900 leading-snug">{s.t}</h3>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.d}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="mt-12 flex justify-center">
          <a href={BRAND.register} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-cta`}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#F26B2A] to-[#F5843C] hover:from-[#E05A19] hover:to-[#EE7630] text-white font-bold px-7 py-4 shadow-[0_12px_30px_-12px_rgba(242,107,42,0.8)] transition-all active:scale-[0.98]">
            <Sparkles className="h-5 w-5" /> {cta} <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </div>
    </section>
  );
}

export function AgentLinkSection({ lang }) {
  const t = T[lang] || T.ru;
  const m = t.m;
  return (
    <StepsSection
      testid="postad-agent-link-section"
      kicker={t.a_kicker} titleA={t.a_title_a} titleB={t.a_title_b} lead={t.a_lead} steps={t.a_steps} cta={t.a_cta}
      tone="bg-[#FFFBF8] border-t border-orange-100/70"
      mocks={[() => <MockCreate m={m} />, () => <MockLink m={m} />, () => <MockAvailability m={m} />, () => <MockRequests m={m} />]}
    />
  );
}

export function TenantScreeningSection({ lang }) {
  const t = T[lang] || T.ru;
  const m = t.m;
  return (
    <StepsSection
      testid="postad-screening-section"
      kicker={t.b_kicker} titleA={t.b_title_a} titleB={t.b_title_b} lead={t.b_lead} steps={t.b_steps} cta={t.b_cta}
      tone="bg-gradient-to-b from-[#FFF3EA] to-[#FFFBF8] border-y border-orange-100/70"
      mocks={[() => <MockQuestions m={m} />, () => <MockAnswer m={m} />, () => <MockReview m={m} />, () => <MockDecide m={m} />]}
    />
  );
}
